import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Copy, AlertCircle, CheckCircle2, Unplug } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TwilioPhoneNumberConnect } from "@/components/TwilioPhoneNumberConnect";
import { VoiceAgent } from "@/services/voiceAgentService";
import { useToast } from "@/hooks/use-toast";
import { usePhoneNumberMutations } from "@/hooks/voice/usePhoneNumberMutations";

interface PhoneNumberStatusCardProps {
  agent: VoiceAgent;
  onAgentUpdate: (updatedAgent: VoiceAgent) => void;
}

export default function PhoneNumberStatusCard({
  agent,
  onAgentUpdate,
}: PhoneNumberStatusCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();
  const { disconnectPhone } = usePhoneNumberMutations();

  const hasPhoneNumber = !!agent.phone_number;
  const isPublished = !!agent.vapi_assistant_id;
  const hasStoredCredentials =
    agent.phone_number && agent.twilio_account_sid && agent.twilio_auth_token;
  const isReconnectScenario =
    hasStoredCredentials && !agent.vapi_phone_number_id;

  const copyPhoneNumber = async () => {
    if (agent.phone_number) {
      try {
        await navigator.clipboard.writeText(agent.phone_number);
        toast({
          title: "Phone number copied",
          description: "Phone number has been copied to clipboard",
        });
      } catch (error) {
        console.error("Failed to copy phone number:", error);
        toast({
          variant: "destructive",
          title: "Copy failed",
          description: "Failed to copy phone number to clipboard",
        });
      }
    }
  };

  const handleDisconnectPhoneNumber = () => {
    if (!agent.id) return;

    disconnectPhone.mutate(
      { agentId: agent.id },
      {
        onSuccess: (data) => {
          onAgentUpdate(data);
          setIsModalOpen(false);
        },
      },
    );
  };

  const getStatusContent = () => {
    if (!isPublished) {
      return {
        icon: <AlertCircle className="h-4 w-4 text-red-500" />,
        text: "Publish agent first",
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
      };
    }

    if (isReconnectScenario) {
      return {
        icon: <Unplug className="h-4 w-4 text-amber-500" />,
        text: "Disconnected - Click to reconnect",
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
      };
    }

    if (!hasPhoneNumber) {
      return {
        icon: <AlertCircle className="h-4 w-4 text-red-500" />,
        text: "Connect a phone number",
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
      };
    }

    return {
      icon: (
        <div className="relative">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
        </div>
      ),
      text: agent.phone_number,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
    };
  };

  const statusContent = getStatusContent();

  const handleCardClick = () => {
    if (!isPublished) {
      // Don't open modal if not published
      return;
    }
    setIsModalOpen(true);
  };

  const getTooltipContent = () => {
    if (!isPublished) {
      return "Publish your agent first";
    }
    return null;
  };

  const CardWrapper = ({ children }: { children: React.ReactNode }) => {
    const tooltipContent = getTooltipContent();

    if (tooltipContent) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent>
              <p>{tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <>{children}</>;
  };

  return (
    <>
      <CardWrapper>
        <Card
          className={`${statusContent.borderColor} ${statusContent.bgColor} transition-all duration-200 ${
            isPublished
              ? "cursor-pointer hover:shadow-md"
              : "cursor-not-allowed opacity-75"
          } h-9 rounded-lg`}
          onClick={handleCardClick}
        >
          <div className="h-full flex items-center justify-between px-3">
            <div className="flex items-center gap-2">
              {statusContent.icon}
              <span className={`text-sm font-medium ${statusContent.color}`}>
                {statusContent.text}
              </span>
            </div>

            {hasPhoneNumber && isPublished && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  copyPhoneNumber();
                }}
                className="h-6 w-6 p-0 hover:bg-green-100"
              >
                <Copy className="h-3 w-3" />
              </Button>
            )}

            {!hasPhoneNumber && <Phone className="h-4 w-4 text-gray-400" />}
          </div>
        </Card>
      </CardWrapper>

      {/* Phone Number Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              {isReconnectScenario
                ? "Reconnect Phone Number"
                : "Connect Phone Number"}
            </DialogTitle>
            <DialogDescription>
              {isReconnectScenario
                ? "Your phone number was disconnected. Reconnect it to start receiving calls again."
                : hasPhoneNumber
                  ? "Your agent will answer calls to this number automatically."
                  : "Connect a phone number so customers can call your AI agent directly."}
            </DialogDescription>
          </DialogHeader>

          {hasPhoneNumber && agent.vapi_phone_number_id ? (
            // Connected state - show current number and disconnect option
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                    <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <p className="font-medium text-green-900">Connected</p>
                    <p className="text-lg font-mono text-green-700">
                      {agent.phone_number}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyPhoneNumber}
                  className="hover:bg-green-100"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Incoming calls are answered by your AI agent</li>
                  <li>• Conversations are recorded and transcribed</li>
                  <li>• Form data is extracted automatically</li>
                </ul>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="destructive"
                  onClick={handleDisconnectPhoneNumber}
                  disabled={disconnectPhone.isPending}
                  className="flex items-center gap-2"
                >
                  {disconnectPhone.isPending ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Unplug className="h-4 w-4" />
                      Disconnect Phone Number
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // Not connected state - show connection form
            <div className="space-y-4">
              <TwilioPhoneNumberConnect
                voiceAgent={agent}
                onSuccess={(updatedAgent) => {
                  onAgentUpdate(updatedAgent);
                  toast({
                    title: "Phone number connected",
                    description:
                      "Your voice agent is now connected to the phone number.",
                  });
                  setIsModalOpen(false);
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
